/**
 * The locale cookie, `mauvely_locale`.
 *
 * On mauvely.com the locale is in the path and this is only what the front
 * door falls back to. Here there is no path prefix — this app is behind a
 * sign-in, nothing about it is indexed, and a prefix would cost a locale-aware
 * router and every bookmarked deep link to buy nothing — so the cookie is the
 * whole answer rather than a fallback.
 *
 * Scoped to `.mauvely.com`, which is why this works at all: somebody who
 * picked their language in mauvely.com's footer arrives here with it already
 * chosen, and never has to answer twice.
 */

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  cookieDomainAttr,
  isLocale
} from '@shared/locales';

/** Last cookie of the name wins, as in lib/consent.ts: a host-only cookie set
 *  before a deploy that introduced the domain attribute would otherwise shadow
 *  the domain one for ever. */
function cookieLocale(): string | null {
  try {
    let found: string | null = null;
    for (const part of document.cookie.split(';')) {
      const [name, ...rest] = part.trim().split('=');
      const value = rest.join('=');
      if (name === LOCALE_COOKIE && isLocale(value)) found = value;
    }
    return found;
  } catch {
    return null;
  }
}

/** The locale being read. Read at call time, never closed over. */
export function currentLocale(): string {
  return cookieLocale() ?? DEFAULT_LOCALE;
}

export function hasLocaleCookie(): boolean {
  return cookieLocale() !== null;
}

export function writeLocaleCookie(tag: string): void {
  if (!isLocale(tag)) return;
  try {
    document.cookie =
      `${LOCALE_COOKIE}=${tag}; Path=/${cookieDomainAttr(window.location.hostname)}` +
      `; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax` +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  } catch {
    // Cookies disabled: the choice lasts this tab and no longer, which is the
    // right behaviour rather than a bug.
  }
}
